import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    ArrowLeft,
    User,
    Mail,
    Phone,
    Calendar,
    ClipboardList,
    TrendingUp,
    MessageCircle,
    Crown,
    MoreVertical,
    Edit,
    Loader2,
    Dumbbell,
    Clock,
    CheckCircle,
    Target,
    Activity,
    Camera,
    Scale,
    Ruler,
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

interface AthleteProfile {
    id: string;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string | null;
    created_at: string;
    pairing: {
        id: string;
        status: string;
        is_premium_seat: boolean;
        paired_at: string;
        notes: string | null;
        invite_code: string | null;
    };
}

interface Prescription {
    id: string;
    workout: {
        id: string;
        name: string;
        duration_minutes: number | null;
    };
    start_date: string;
    end_date: string | null;
    frequency_per_week: number;
    is_active: boolean;
}

interface WorkoutLog {
    id: string;
    workout_name: string;
    completed_at: string;
    duration_seconds: number | null;
    perceived_effort: number | null;
}

interface ProgressEntry {
    id: string;
    type: string;
    value: number;
    unit: string;
    recorded_at: string;
}

export function AthleteDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { tenant } = useAuth();
    const [activeTab, setActiveTab] = useState<'overview' | 'workouts' | 'progress'>('overview');

    // Fetch student data
    const { data: student, isLoading } = useQuery({
        queryKey: ['student', id, tenant?.id],
        queryFn: async () => {
            if (!tenant?.id || !id) return null;

            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    id,
                    status,
                    is_premium_seat,
                    paired_at,
                    notes,
                    invite_code,
                    student:student_id (
                        id,
                        full_name,
                        email,
                        phone,
                        avatar_url,
                        created_at
                    )
                `)
                .eq('tenant_id', tenant.id)
                .eq('student_id', id)
                .single();

            if (error) throw error;

            return {
                ...(data.student as unknown as Record<string, unknown>),
                pairing: {
                    id: data.id,
                    status: data.status,
                    is_premium_seat: data.is_premium_seat,
                    paired_at: data.paired_at,
                    notes: data.notes,
                    invite_code: data.invite_code,
                },
            } as AthleteProfile;
        },
        enabled: !!tenant?.id && !!id,
    });

    // Fetch prescriptions
    const { data: prescriptions } = useQuery({
        queryKey: ['prescriptions', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_prescriptions')
                .select(`
                    id,
                    start_date,
                    end_date,
                    frequency_per_week,
                    is_active,
                    workout:workout_id (
                        id,
                        name,
                        duration_minutes
                    )
                `)
                .eq('student_id', id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data as unknown as Prescription[];
        },
        enabled: !!id,
    });

    // Fetch workout logs
    const { data: workoutLogs } = useQuery({
        queryKey: ['workout-logs', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_logs')
                .select('*')
                .eq('student_id', id)
                .order('completed_at', { ascending: false })
                .limit(20);

            if (error) throw error;
            return data as WorkoutLog[];
        },
        enabled: !!id,
    });

    // Fetch progress entries
    const { data: progressEntries } = useQuery({
        queryKey: ['progress', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('progress_entries')
                .select('*')
                .eq('user_id', id)
                .order('recorded_at', { ascending: false })
                .limit(50);

            if (error) throw error;
            return data as ProgressEntry[];
        },
        enabled: !!id,
    });

    // Fetch workout logs for volume chart
    const { data: volumeHistory } = useQuery({
        queryKey: ['athlete-volume-history', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_logs')
                .select(`
                    id,
                    completed_at,
                    sets:workout_log_sets(reps_completed, weight_kg)
                `)
                .eq('student_id', id)
                .order('completed_at', { ascending: true });

            if (error) throw error;

            // Calculate daily volume
            const volumeData = data.reduce((acc: any[], log: any) => {
                const date = new Date(log.completed_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                const volume = log.sets.reduce((sum: number, set: any) => sum + (set.reps_completed * (set.weight_kg || 0)), 0);

                const existing = acc.find(a => a.date === date);
                if (existing) {
                    existing.volume += volume;
                } else {
                    acc.push({ date, volume });
                }
                return acc;
            }, []);

            return volumeData;
        },
        enabled: !!id,
    });

    // Stats
    const completedWorkouts = workoutLogs?.length || 0;
    const activePrescriptions = prescriptions?.filter((p) => p.is_active)?.length || 0;
    const weightEntries = progressEntries?.filter((p) => p.type === 'weight') || [];
    const latestWeight = weightEntries[0]?.value;
    const firstWeight = weightEntries[weightEntries.length - 1]?.value;
    const weightChange = latestWeight && firstWeight ? latestWeight - firstWeight : null;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (!student) {
        return (
            <div className="text-center py-12">
                <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">Atleta não encontrado</h3>
                <button onClick={() => navigate('/atletas')} className="btn-primary">
                    Voltar para Atletas
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/atletas')}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <h1 className="section-title">{student.full_name}</h1>
                        {student.pairing.is_premium_seat && (
                            <span className="badge badge-primary flex items-center gap-1">
                                <Crown className="w-3 h-3" /> Premium
                            </span>
                        )}
                    </div>
                    <p className="section-subtitle">
                        Atleta desde {new Date(student.pairing.paired_at).toLocaleDateString('pt-BR', {
                            month: 'long',
                            year: 'numeric',
                        })}
                    </p>
                </div>
                <button className="btn-secondary">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Mensagem
                </button>
            </div>

            {/* Profile Card */}
            <div className="card bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
                        {student.avatar_url ? (
                            <img src={student.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                            student.full_name?.charAt(0).toUpperCase()
                        )}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                        <h2 className="text-xl font-bold text-gray-900">{student.full_name}</h2>
                        <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {student.email}
                            </span>
                            {student.phone && (
                                <span className="flex items-center gap-1">
                                    <Phone className="w-4 h-4" />
                                    {student.phone}
                                </span>
                            )}
                        </div>
                    </div>
                    <button className="btn-ghost">
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <StatCard
                    icon={<CheckCircle className="w-5 h-5" />}
                    label="Treinos Concluídos"
                    value={String(completedWorkouts)}
                    color="green"
                />
                <StatCard
                    icon={<ClipboardList className="w-5 h-5" />}
                    label="Treinos Ativos"
                    value={String(activePrescriptions)}
                    color="blue"
                />
                <StatCard
                    icon={<Scale className="w-5 h-5" />}
                    label="Peso Atual"
                    value={latestWeight ? `${latestWeight} kg` : 'N/A'}
                    color="purple"
                />
                <StatCard
                    icon={<TrendingUp className="w-5 h-5" />}
                    label="Variação"
                    value={weightChange !== null ? `${weightChange > 0 ? '+' : ''}${weightChange.toFixed(1)} kg` : 'N/A'}
                    color={weightChange && weightChange < 0 ? 'green' : 'yellow'}
                />
            </div>

            {/* Tabs */}
            <div className="border-b">
                <nav className="flex gap-8">
                    {[
                        { id: 'overview', label: 'Visão Geral', icon: Activity },
                        { id: 'workouts', label: 'Treinos', icon: Dumbbell },
                        { id: 'progress', label: 'Progresso', icon: TrendingUp },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as typeof activeTab)}
                            className={`flex items-center gap-2 py-4 border-b-2 font-medium transition-colors ${activeTab === tab.id
                                ? 'border-primary-600 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Recent Activity */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5" />
                            Atividade Recente
                        </h3>
                        {workoutLogs && workoutLogs.length > 0 ? (
                            <div className="space-y-3">
                                {workoutLogs.slice(0, 5).map((log) => (
                                    <div key={log.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">{log.workout_name || 'Treino'}</p>
                                            <p className="text-sm text-gray-500">
                                                {new Date(log.completed_at).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                        {log.duration_seconds && (
                                            <span className="text-sm text-gray-500">
                                                {Math.floor(log.duration_seconds / 60)} min
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center py-8">Nenhuma atividade registrada</p>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Edit className="w-5 h-5" />
                            Anotações
                        </h3>
                        {student.pairing.notes ? (
                            <p className="text-gray-600">{student.pairing.notes}</p>
                        ) : (
                            <p className="text-gray-500 text-center py-8">Nenhuma anotação</p>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'workouts' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold">Treinos Prescritos</h3>
                        <Link to="/workouts" className="btn-primary btn-sm">
                            <ClipboardList className="w-4 h-4 mr-1" />
                            Prescrever Novo
                        </Link>
                    </div>

                    {prescriptions && prescriptions.length > 0 ? (
                        <div className="grid gap-4 md:grid-cols-2">
                            {prescriptions.map((prescription) => (
                                <div
                                    key={prescription.id}
                                    className={`card ${prescription.is_active ? 'border-green-200 bg-green-50' : ''}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">
                                                {prescription.workout?.name || 'Treino'}
                                            </h4>
                                            <p className="text-sm text-gray-500">
                                                {prescription.frequency_per_week}x por semana
                                            </p>
                                        </div>
                                        {prescription.is_active ? (
                                            <span className="badge bg-green-100 text-green-700">Ativo</span>
                                        ) : (
                                            <span className="badge badge-secondary">Inativo</span>
                                        )}
                                    </div>
                                    <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {new Date(prescription.start_date).toLocaleDateString('pt-BR')}
                                        </span>
                                        {prescription.workout?.duration_minutes && (
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {prescription.workout.duration_minutes} min
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="card text-center py-12">
                            <Dumbbell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500">Nenhum treino prescrito ainda</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'progress' && (
                <div className="space-y-6">
                    {/* Evolution Charts */}
                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Weight Chart */}
                        <div className="card">
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Scale className="w-5 h-5 text-primary-600" />
                                Evolução de Peso
                            </h3>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={progressEntries?.filter(e => e.type === 'weight').slice(0, 10).reverse().map(e => ({
                                        date: new Date(e.recorded_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                                        peso: e.value
                                    }))}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                                        <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value: any) => [`${value} kg`, 'Peso']}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="peso"
                                            stroke="#ec4899"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: '#ec4899', strokeWidth: 2, stroke: '#fff' }}
                                            activeDot={{ r: 6, fill: '#ec4899' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Volume Chart */}
                        <div className="card">
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                                Volume de Treino
                            </h3>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={volumeHistory?.slice(-7)}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                                        <YAxis hide />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value: any) => [`${value} kg`, 'Volume Total']}
                                        />
                                        <Bar dataKey="volume" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Progress Photos */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Camera className="w-5 h-5" />
                            Fotos de Progresso
                        </h3>
                        <div className="text-center py-8 text-gray-500">
                            <Camera className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                            <p>Nenhuma foto de progresso disponível</p>
                        </div>
                    </div>

                    {/* Measurements */}
                    <div className="card">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Ruler className="w-5 h-5" />
                            Medidas Corporais
                        </h3>
                        {progressEntries && progressEntries.length > 0 ? (
                            <div className="grid gap-4 md:grid-cols-3">
                                {['weight', 'chest', 'waist', 'hips'].map((type) => {
                                    const entries = progressEntries.filter((e) => e.type === (type === 'weight' ? 'weight' : 'measurement'));
                                    const latest = entries[0];

                                    let value = latest?.value;
                                    let unit = latest?.unit;

                                    if (type !== 'weight' && (latest as any)?.metadata) {
                                        const metadata = (latest as any).metadata;
                                        value = metadata[type];
                                    }

                                    if (!value) return null;

                                    return (
                                        <div key={type} className="p-4 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-500 capitalize">{type === 'weight' ? 'Peso' : type === 'chest' ? 'Peito' : type === 'waist' ? 'Cintura' : 'Quadril'}</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {value} {unit || (type === 'weight' ? 'kg' : 'cm')}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(latest.recorded_at).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Scale className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                                <p>Nenhuma medida registrada</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Stat Card Component
function StatCard({
    icon,
    label,
    value,
    color,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    color: 'green' | 'blue' | 'purple' | 'yellow';
}) {
    const colorClasses = {
        green: 'bg-green-100 text-green-600',
        blue: 'bg-blue-100 text-blue-600',
        purple: 'bg-purple-100 text-purple-600',
        yellow: 'bg-yellow-100 text-yellow-600',
    };

    return (
        <div className="card flex items-center gap-4">
            <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
                {icon}
            </div>
            <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-xl font-bold text-gray-900">{value}</p>
            </div>
        </div>
    );
}
